import type { Component } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';
import { resolveEffectiveType } from '@Src/shared/utils/resolveEffectiveType';
import EsignIllustration from './EsignIllustration.vue';
import PaymentPrimingIllustration from './PaymentPrimingIllustration.vue';
import SendPrimingIllustration from './SendPrimingIllustration.vue';
import IllustrationIdvPassportPriming from './illustrations/IllustrationIdvPassportPriming.vue';
import IllustrationIdvPhoneScanPriming from './illustrations/IllustrationIdvPhoneScanPriming.vue';

export interface PrimingItem {
    // uilib icon name (e.g. 'cd-ic-bank'); rendered via <app-icon>.
    // Required unless `illustration` is provided (which replaces it entirely).
    icon?: string;
    label?: string;
    description: string;
    // Optional custom illustration component rendered in place of the icon.
    illustration?: Component;
}

export interface PrimingWarning {
    title: string;
    description: string;
}

export interface PrimingConfig {
    title: string;
    subtitle?: string;
    illustration?: Component;
    items?: PrimingItem[];
    warning?: PrimingWarning;
    ctaLabel: string;
    termsLinkUrl?: string;
    termsLabel?: string;
    // Standalone help link rendered below the CTA (the link text IS the
    // hyperlink — distinct from termsLabel + "Learn more"). Per Figma the IDV
    // primer shows "Why am I doing this?" as its own link line.
    helpLink?: {
        label: string;
        url: string;
    };
    // When true, TaskPrimingSheet renders the "By clicking '<ctaLabel>' you
    // agree to our Terms of Use and Privacy Policy." consent caption with
    // links to the slug-prefixed legal routes. Required for IDV (biometric
    // consent must be captured at the point of collection — see the Biometric
    // Information Privacy Policy in legal/privacy.content.html Part III).
    // The URLs are runtime values (company slug via getBasePath()), so the
    // sheet owns link construction; the config only opts in.
    showLegalConsent?: boolean;
}

// Closing-funds priming, shared by the payment_c2c task type and by a non-EMD
// payment_request (cash-to-close, digital payments enabled). Defined once and
// referenced under both keys below so the two can never drift.
const closingFundsPriming: PrimingConfig = {
    title: 'Send your closing funds',
    illustration: PaymentPrimingIllustration,
    ctaLabel: 'Start payment',
    items: [
        {
            icon: 'cd-ic-bank',
            description: 'Connect your bank via Plaid to pay instantly, or view wire instructions for a traditional bank transfer.',
        },
        {
            icon: 'cd-ic-paper-plane',
            description: 'Send funds securely to your title company’s verified bank account',
        },
    ],
};

// Identity verification priming (DS-5345), shared by `match` and
// `complete_match_request` below. Figma: "Continue to instructions"
// (node 7001:110901) — "Let's get prepared" bottom sheet.
const idvPriming: PrimingConfig = {
    title: 'Let\'s get prepared',
    subtitle: 'Here\'s what you will need and what to expect when verifying your identity:',
    ctaLabel: 'Continue',
    helpLink: {
        label: 'Why am I doing this?',
        url: 'https://certifidhelpcenter.zendesk.com/hc/en-us/articles/18426417319579-I-don-t-feel-comfortable-providing-my-personal-information',
    },
    showLegalConsent: true,
    items: [
        {
            illustration: IllustrationIdvPassportPriming,
            label: 'Scan valid ID',
            description: 'You\'ll need to scan a valid ID, like your drivers license or passport \u2013 so grab this before you start.',
        },
        {
            illustration: IllustrationIdvPhoneScanPriming,
            label: 'Take a photo',
            description: 'You\'ll need to take a photo of your face with your device. Make sure you have good lighting.',
        },
    ],
};

const emdPriming: PrimingConfig = {
    title: 'Send your earnest money deposit',
    illustration: PaymentPrimingIllustration,
    ctaLabel: 'Start payment',
    items: [
        {
            icon: 'cd-ic-bank',
            description: 'Connect your bank via Plaid.',
        },
        {
            icon: 'cd-ic-paper-plane',
            description: 'Confirm and send your deposit.',
        },
    ],
};

// Keyed by production TaskItem.taskType (not prototype keys).
// Any task type absent from this map skips the priming sheet entirely
// (caller falls back to direct navigation — see useTaskNavigation).
const PRIMING_CONFIGS: Record<string, PrimingConfig> = {
    document_signing: {
        title: 'Sign your closing documents',
        subtitle: 'DocuSign will guide you through each signature required for this closing.',
        illustration: EsignIllustration,
        ctaLabel: 'Continue to DocuSign',
        termsLinkUrl: 'https://www.docusign.com/legal/terms-and-conditions',
        termsLabel: 'By continuing, you agree to the DocuSign Terms of Use.',
    },
    payment_emd: emdPriming,
    payment_c2c: closingFundsPriming,
    // A cash-to-close payment_request is the existing closing-funds flow, so it reuses
    // the payment_c2c priming. DS-3309 splits EMD off into payment_request_emd below;
    // the cash-to-close split (payment_request_c2c) keeps this closing-funds priming.
    payment_request: closingFundsPriming,
    payment_request_c2c: closingFundsPriming,
    // No entry for payment_request_other or payment_other: closing-funds and EMD copy must not reach an Other payment.
    // Re-paying a closing payment whose bank debit was returned after funds were
    // already delivered. No illustration or generic items -- TaskPrimingSheet renders
    // the co-brand lockup and dynamic explanation (company name interpolated) in their
    // place. Leaving `illustration` absent is load-bearing: it keeps hasIllustration
    // false so the sheet uses the centered (non-illustrated) layout -- don't re-add one
    // without updating TaskPrimingSheet.
    payment_repayment: {
        title: 'Resubmit your payment to CertifID',
        ctaLabel: 'Resubmit payment',
    },
    send: {
        title: 'We only share verified wire instructions',
        subtitle: 'By continuing, you agree to use only these verified instructions for your wire transfer.',
        illustration: SendPrimingIllustration,
        ctaLabel: 'View instructions',
        warning: {
            title: 'Wire instructions will never change',
            description: 'These instructions are final and will never be updated by email, call, or text. If anyone claims otherwise, do not send funds.',
        },
    },
    payment_request_emd: emdPriming,
    collect: {
        title: 'Share your bank details safely',
        subtitle: 'You’ll need the bank account details for this closing, so get them ready before you start.',
        illustration: SendPrimingIllustration,
        ctaLabel: 'Provide bank details',
        items: [
            {
                icon: 'cd-ic-check-shield',
                label: 'Verified by CertifID',
                description: 'Your information is encrypted and only shared with the title company handling your closing.',
            },
        ],
    },
    confirm: {
        title: 'Confirm your bank details',
        subtitle: 'Check that we have the correct bank information for your transaction.',
        illustration: SendPrimingIllustration,
        ctaLabel: 'Review bank details',
        items: [
            {
                icon: 'cd-ic-check-shield',
                label: 'Verified by CertifID',
                description: 'Confirming these details helps prevent wire fraud and misdirected payments.',
            },
        ],
    },
    // Identity verification priming (DS-5345). Both `match` (active task) and
    // `complete_match_request` (completed task that the participant is re-entering
    // after a failed verification) reference this single const so copy can never drift.
    match: idvPriming,
    complete_match_request: idvPriming,
};

export function resolvePrimingConfig(task: TaskItem): PrimingConfig | null {
    const effective = resolveEffectiveType(task);
    if (!effective) return null;
    return PRIMING_CONFIGS[effective] ?? null;
}

export function hasPrimingConfig(task: TaskItem): boolean {
    return resolvePrimingConfig(task) !== null;
}
